import type { Document as ContentfulDocument } from "@contentful/rich-text-types";

import { NewsEntryArticle, SmallNewsEntryArticle } from "../graphql";
import { GameName, NewsEntryDetails, Section } from "../types";
import {
  identifyVideoProvider,
  transformRichText,
  getPollOptions,
  transformImageWithFallback,
  transformUnifiedButton,
} from "./transforms";
import { GameResolution } from "../gameSettings/types";
import { Locale } from "../locale";

export const convertNewsEntryBodyToDetails = (
  content: NewsEntryArticle | SmallNewsEntryArticle,
  transformFunction: ReturnType<typeof transformImageWithFallback>,
  resolutions: GameResolution,
  gameName: GameName,
  topLevelEntryId: string,
  newsEntryIds: string[],
  section: Section,
  locale: Locale,
): NewsEntryDetails => {
  if (content === null) {
    return [];
  }

  if (!content.bodyCollection) {
    return [];
  }

  const { items } = content.bodyCollection;

  const existingItems = items.filter(
    (v): v is NonNullable<typeof v> => v !== null,
  );

  let pollIndex = 0;
  return existingItems.reduce<NewsEntryDetails>((acc, block, idx) => {
    switch (block.__typename) {
      case "TextBlock": {
        const document = block?.text?.json as ContentfulDocument | undefined;

        const html = document
          ? transformRichText(document, gameName, locale)
          : "";

        const formattedBlock = {
          title: block.title ?? "",
          type: "textBlock",
          body: html,
          accordion: block.accordion ?? "no",
        };

        return [...acc, formattedBlock];
      }

      case "ImageBlock": {
        const formattedBlock = {
          title: null,
          author: block.author ?? undefined,
          fullWidth: block.fullWidth ?? "no",
          type: "imageBlock",
          image: transformFunction(block, "image", resolutions.imageBlock),
        };

        return [...acc, formattedBlock];
      }

      case "UnifiedButton": {
        const unifiedButton = transformUnifiedButton(
          gameName,
          block,
          idx.toString(),
          newsEntryIds,
          section,
        );

        if (!unifiedButton) {
          return acc;
        }

        return [...acc, { ...unifiedButton, type: "buttonBlock" }];
      }

      case "VideoBlock": {
        const formattedBlock = {
          title: block.title ?? "",
          type: "videoBlock",
          author: "",
          embed: {
            url: block.videoUrl,
            provider: identifyVideoProvider(block.videoUrl),
            id: "",
          },
          embedVideo: false,
          thumbnail: transformFunction(
            block,
            "videoThumbnail",
            resolutions.videoBlock,
          ),
        };

        return [...acc, formattedBlock];
      }

      case "PollBlock": {
        const formattedBlock = {
          title: block.pollTitle ?? "",
          type: "pollBlock",
          poll: {
            pollId: `${topLevelEntryId}-${pollIndex}`,
            options: getPollOptions(
              block,
              transformFunction,
              resolutions,
              "pollBlock",
            ),
          },
        };

        pollIndex += 1;

        return [...acc, formattedBlock];
      }

      case "LeaderboardBlock": {
        const formattedBlock = {
          title: block.title ?? "",
          type: "leaderboardBlock",
          data: block.leaderboardData,
          configuration: {
            ...block.leaderboardConfiguration,
            defaultRows: block.defaultRows ?? 10,
            maximumRows: block.maximumRows ?? 100,
            columns: block.leaderboardConfiguration?.columns.map((column) => ({
              ...column,
              image: transformFunction(
                {
                  image: column.image,
                  imageEn: column.image,
                },
                "image",
                resolutions.leaderboardBlock,
              ),
            })),
          },
        };

        return [...acc, formattedBlock];
      }

      case "FeatureBlock": {
        const document = block?.featureText?.json as
          | ContentfulDocument
          | undefined;

        const html = document
          ? transformRichText(document, gameName, locale)
          : "";

        const formattedBlock = {
          title: block.title ?? "",
          position: block.featureThumbnailPosition ?? "left",
          type: "featureBlock",
          body: html,
          image: transformFunction(
            block,
            "featureThumbnail",
            resolutions.featureBlock,
          ),
          accordion: block.accordion ?? "no",
        };

        return [...acc, formattedBlock];
      }
    }
  }, []);
};
